import type { TFunction } from 'i18next';

import { ETheme } from '@feathr/components';
import type { IChart } from '@feathr/extender/components/AudienceAreaChart';

import type { TView } from '../AudienceEngagement';

interface IChartsProps {
  t: TFunction;
  view: TView;
}

const { green, purple, cyan } = ETheme;

export const primaryChart = ({ view, t }: IChartsProps): IChart | null => {
  const map: Record<TView, IChart | null> = {
    community: {
      label: t('Persons created'),
      tooltip: t(
        'The total number of people who have been added to your account through any source.',
      ),
      theme: purple,
      yAxisKey: 'num_persons_created',
    },
    conversions: {
      label: t('Total conversions'),
      tooltip: t('The total number of conversions that have been tracked in your account.'),
      theme: green,
      yAxisKey: 'num_conversions',
    },
    connections: null,
  };

  return map[view];
};

export const secondaryCharts = ({ view, t }: IChartsProps): IChart[] => {
  const map: Record<TView, IChart[]> = {
    community: [
      {
        label: t('By audience expansion campaigns'),
        tooltip: t(
          'The number of people who have been added to your account through audience expansion campaigns.',
        ),
        theme: purple,
        yAxisKey: 'num_persons_created_by_expansion',
      },
      {
        label: t('By imports and integrations'),
        tooltip: t(
          'The number of people who have been added to your account through imports and data integrations.',
        ),
        theme: purple,
        yAxisKey: 'num_persons_created_by_import',
      },
      {
        label: t('By pixel'),
        tooltip: t(
          'The number of people who have been added to your account through our tracking pixel.',
        ),
        theme: purple,
        yAxisKey: 'num_persons_created_by_pixel',
      },
    ],
    connections: [
      {
        label: t('Ad clicks'),
        tooltip: t(
          'The number of times people in your audience have clicked ads from ad campaigns.',
        ),
        theme: cyan,
        yAxisKey: 'num_persons_with_ad_clicks',
      },
      {
        label: t('Ad views'),
        tooltip: t(
          'The number of times people in your audience have viewed ads from ad campaigns.',
        ),
        theme: cyan,
        yAxisKey: 'num_persons_with_ad_views',
      },
      {
        label: t('Email opens'),
        tooltip: t('The number of times people in your audience have opened your emails.'),
        theme: cyan,
        yAxisKey: 'num_pinpoint_tracked_email_opens',
      },
      {
        label: t('Email clicks'),
        tooltip: t(
          'The number of times people in your audience have clicked on links in your emails.',
        ),
        theme: cyan,
        yAxisKey: 'num_pinpoint_tracked_email_clicks',
      },
      {
        label: t('Page views'),
        tooltip: t('The number of times people in your audience have visited tracked pages.'),
        theme: cyan,
        yAxisKey: 'num_persons_with_page_views',
      },
      {
        label: t('Page link clicks'),
        tooltip: t(
          'The number of times people in your audience have clicked on links on tracked pages.',
        ),
        theme: cyan,
        yAxisKey: 'num_persons_with_page_link_clicks',
      },
    ],
    // TODO: Update all yAxisKeys in `conversions` to be the correct stat keys in https://github.com/Feathr/shrike/issues/3421.
    conversions: [
      {
        label: t('Goal A'),
        tooltip: t(
          'The total number of people who have been added to your account through any source.',
        ),
        theme: green,
        yAxisKey: 'x1',
      },
      {
        label: t('Goal B'),
        tooltip: t(
          'The total number of people who have been added to your account through any source.',
        ),
        theme: green,
        yAxisKey: 'x2',
      },
      {
        label: t('Goal C'),
        tooltip: t(
          'The total number of people who have been added to your account through any source.',
        ),
        theme: green,
        yAxisKey: 'x3',
      },
    ],
  };

  return map[view];
};
